import type { BankAccount, Card, UpdateBankAccountBody } from '@klopotun/contracts';
import { SelectField, ToggleField, type SelectOption } from '../../../../shared/ui/index.js';
import styles from './bank-account-row.module.css';

type BankAccountRowProps = {
  account: BankAccount;
  cards: Card[];
  busy: boolean;
  onChange: (body: UpdateBankAccountBody) => void;
};

const descriptionOf = (account: BankAccount): string =>
  [
    account.currency ?? 'валюта, якої застосунок ще не знає',
    account.last4 === null ? null : `•••• ${account.last4}`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

const dayOf = (iso: string): string => new Date(iso).toLocaleDateString('uk-UA');

const historyOf = (account: BankAccount): string => {
  const operations = `операцій: ${String(account.operationCount)}`;

  if (account.historyFrom === null) {
    return 'Історія ще не завантажувалась';
  }

  if (!account.isHistoryComplete) {
    return `Історія завантажується… ${operations}, з ${dayOf(account.historyFrom)}`;
  }

  return `Історія завантажена: ${operations}, з ${dayOf(account.historyFrom)}`;
};

const cardOptionsOf = (cards: Card[], account: BankAccount): SelectOption[] =>
  cards
    .filter((card) => !card.isArchived || card.id === account.cardId)
    .map((card) => ({
      value: card.id,
      label: card.last4 === null ? card.label : `${card.label} · ${card.last4}`,
    }));

export const BankAccountRow = ({ account, cards, busy, onChange }: BankAccountRowProps) => (
  <div className={styles.row}>
    <ToggleField
      label={account.title}
      name={`bank-account-${account.id}`}
      description={descriptionOf(account)}
      checked={account.isTracked}
      disabled={busy || account.currency === null}
      onChange={(event) => {
        onChange({ isTracked: event.target.checked });
      }}
    />

    {account.isTracked && <p className={styles.history}>{historyOf(account)}</p>}

    {account.isTracked && (
      <SelectField
        label="Картка"
        name={`bank-account-card-${account.id}`}
        options={cardOptionsOf(cards, account)}
        value={account.cardId ?? ''}
        disabled={busy}
        onChange={(event) => {
          onChange({ cardId: event.target.value });
        }}
      />
    )}
  </div>
);
